
import { useState, useRef, useEffect } from 'react';

interface SecurityCard {
  title: string;
  description: string;
  icon: string;
  features: string[];
  color: string;
}

export default function SecuritySolutions() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const solutions: SecurityCard[] = [
    {
      title: "Advanced Threat Detection",
      description: "Real-time monitoring and AI-powered threat identification across your entire infrastructure",
      icon: "ri-shield-check-line",
      features: ["24/7 Monitoring", "AI Analysis", "Real-time Alerts", "Behavioral Analytics"],
      color: "from-blue-600 to-cyan-500"
    },
    {
      title: "Cloud Security",
      description: "Comprehensive protection for cloud environments with multi-layer security protocols",
      icon: "ri-cloud-line",
      features: ["Multi-Cloud Support", "Data Encryption", "Access Control", "Compliance"],
      color: "from-purple-600 to-pink-500"
    },
    {
      title: "Zero Trust Architecture",
      description: "Never trust, always verify approach to secure your network perimeter",
      icon: "ri-lock-line",
      features: ["Identity Verification", "Micro-segmentation", "Continuous Monitoring", "Policy Enforcement"],
      color: "from-green-600 to-teal-500"
    },
    {
      title: "Security Operations Center",
      description: "Centralized security management with expert analysts and automated response",
      icon: "ri-radar-line",
      features: ["Expert Analysts", "Automated Response", "Incident Management", "Threat Intelligence"],
      color: "from-orange-600 to-red-500"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleCards(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = sectionRef.current?.querySelectorAll('.security-card');
    cards?.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Security Solutions
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive security solutions designed to protect your business from evolving cyber threats
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {solutions.map((solution, index) => (
            <div
              key={index}
              data-index={index}
              className={`security-card group relative bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:bg-slate-800/60 transform transition-all duration-700 hover:scale-105 hover:-translate-y-2 cursor-pointer ${
                visibleCards.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              {/* Gradient border on hover */}
              <div className={`absolute inset-0 bg-gradient-to-r ${solution.color} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300 blur-sm`}></div>
              <div className="relative z-10 bg-slate-800/90 rounded-2xl p-8 -m-2">
                
                {/* 3D Icon */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 mx-auto bg-gradient-to-r ${solution.color} rounded-xl flex items-center justify-center group-hover:animate-pulse`}>
                    <i className={`${solution.icon} text-2xl text-white`}></i>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-r ${solution.color} opacity-20 rounded-xl blur-lg group-hover:blur-xl transition-all duration-300`}></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors duration-300">
                  {solution.title}
                </h3>

                <p className="text-gray-400 mb-6 leading-relaxed">
                  {solution.description}
                </p>

                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li 
                      key={featureIndex} 
                      className="flex items-center text-sm text-gray-300 group-hover:text-gray-200 transition-colors duration-300"
                    >
                      <i className="ri-check-line text-green-400 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Hover effect overlay */}
                <div className={`absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r ${solution.color} rounded-full opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-300`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* 3D Security Network Visualization */}
        <div className="mt-16 text-center">
          <div className="relative inline-block">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20network%20security%20visualization%20showing%20interconnected%20nodes%20and%20data%20flow%2C%20glowing%20connections%2C%20futuristic%20holographic%20display%2C%20blue%20and%20purple%20neon%20colors%2C%20transparent%20background%2C%20high-tech%20security%20network%20model%20with%20floating%20elements&width=800&height=400&seq=network3d&orientation=landscape"
              alt="3D Security Network"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-500 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
